import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './EvaluationQuestionEditor.module.scss'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import TextAreaInput from 'components/ui/forms/field/text-area/TextArea'
import Button, {
  ButtonColors,
  ButtonSizes,
  ButtonVariant,
} from 'design-system/button/src/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faCheckCircle,
  faTimes,
  faVideo,
  faImage,
  faCode,
  faPen,
} from '@fortawesome/pro-solid-svg-icons'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import { Alphabet, stringToHtml } from 'helpers/others.helper'
import { ButtonToolbar } from 'react-bootstrap'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import {
  IQuestionData,
  QuestionOptionForm,
  QuestionForm,
} from 'models/question.model'
import EmptyModal from 'components/ui/modals/emptyModal/EmptyModal'
import { validateEvaluationContent } from 'helpers/validators.helper'
import { WarningToast } from 'components/ui/toast/toastsTemplates'
import { EvaluationCreationErrorsEnum } from 'enum/evaluationCreationErrors.enum'

interface IEvaluationQuestionsEditor {
  editing?: number
  onDiscardChanges?: () => void
  onFinish?: (question: QuestionForm) => void
  onCreate?: (question: QuestionForm) => void
  number: number
  question?: IQuestionData
  hasResponses?: boolean
  setConfirmationCallback: (callback: () => void) => void
}

const defaultOptions: QuestionOptionForm[] = [
  { text: '', checked: true },
  { text: '', checked: false },
]

const defaultValues: QuestionForm = {
  text: '',
  link: {
    type: 0,
    url: '',
  },
  explanationAnswer: '',
  order: 0,
  options: defaultOptions,
}

const EvaluationQuestionsEditor: React.FC<IEvaluationQuestionsEditor> = ({
  editing = 0,
  onDiscardChanges,
  onCreate,
  onFinish,
  number,
  question,
  setConfirmationCallback,
}) => {
  const { t } = useTranslation()

  const [data, setData] = useState<QuestionForm>(defaultValues)
  const [options, setOptions] = useState(defaultValues.options || [])
  const [linkOptionSelected, setLinkOptionSelected] = useState(0)
  const [deletedOptions, setDeletedOptions] = useState<QuestionOptionForm[]>([])
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [showDescriptionQuestion, setShowDescriptionQuestion] = useState(false)

  useEffect(() => {
    if (question?.questionableId) {
      const { name, url, urlType, questionableId, options, explanationAnswer } =
        question
      const optionsAux = options.map(
        ({ id = 0, label, checked }): QuestionOptionForm => ({
          id,
          text: label,
          checked,
        })
      )
      setData({
        questionId: questionableId,
        text: name,
        link: {
          url: url,
          type: urlType,
        },
        explanationAnswer: explanationAnswer,
        order: number,
      })
      setLinkOptionSelected(urlType)
      setOptions(optionsAux)
    }
  }, [number, question])

  const handleInputChange = (text: string, index: number) => {
    // deepclone
    const aux = options.map((option) => ({ ...option }))
    aux[index].text = text
    setOptions(aux)
  }

  const handleCheckChange = (selectedIndex: number) => {
    // only one checked
    const aux = options.map(
      ({ id = 0, text }: QuestionOptionForm, index: number) => ({
        id,
        text,
        checked: selectedIndex === index,
      })
    )
    setOptions(aux)
  }

  const addOption = () => {
    const aux = options.slice()
    aux.push({ text: '', checked: false })
    setOptions(aux)
  }

  const deleteOption = (index: number) => {
    let aux = options.slice()
    if (aux[index].checked)
      aux = aux.map(
        ({ id = 0, text }: QuestionOptionForm, currentIndex: number) => ({
          id,
          text,
          checked: currentIndex === index - 1,
        })
      )
    const deleted = aux.splice(index, 1)
    // Se crea un nuevo arreglo para almacenar las opciones eliminadas
    const deletedArrayAux = deletedOptions.slice()
    deletedArrayAux.push({ ...deleted[0], _destroy: true })
    setDeletedOptions(deletedArrayAux)

    setOptions(aux)
  }

  const handleDiscard = () => {
    editing && onDiscardChanges?.()
    setData(defaultValues)
    setOptions(defaultOptions)
    setLinkOptionSelected(0)
  }

  useEffect(() => {
    const textarea = document.getElementById('text-area')
    if (textarea) {
      textarea.style.height = '0px'
      const height =
        textarea.scrollHeight > 54
          ? 54
          : textarea.scrollHeight < 30
          ? 30
          : textarea.scrollHeight
      textarea.style.height = height + 'px'
    }
  }, [data])

  const handleSave = () => {
    const auxData: QuestionForm = {
      ...data,
      options,
      order: number - 1,
      link: {
        ...data.link,
        url: linkOptionSelected ? data.link.url : '',
        type: data.link.url ? linkOptionSelected : 0,
      },
    }
    setConfirmationCallback(() => {
      const error = validateEvaluationContent(auxData)
      if (!error) {
        if (editing) {
          onFinish?.({ ...auxData, options: options.concat(deletedOptions) })
        } else {
          onCreate?.(auxData)
        }
        setData(defaultValues)
        setOptions(defaultOptions)
        setLinkOptionSelected(0)
      } else {
        const message =
          error === EvaluationCreationErrorsEnum.name
            ? 'emptyName'
            : 'emptyOption'
        WarningToast(
          t(`administration.blocks.type.evaluation.toasts.error.${message}`)
        )
      }
    })
  }

  const modalMessage = () => {
    return (
      <div className="modalContent">
        <b>
          <p>
            {t(
              'administration.blocks.type.evaluation.helpers.whatIsThisMessage.title'
            )}
          </p>
        </b>
        <p>
          {t(
            'administration.blocks.type.evaluation.helpers.whatIsThisMessage.part1'
          )}
        </p>
        <div className={`${styles.linksExample} mb-3`}>
          <div
            className="m-0"
            dangerouslySetInnerHTML={{
              __html: stringToHtml(
                t(
                  'administration.blocks.type.evaluation.helpers.whatIsThisMessage.part2'
                )
              ),
            }}
          />
          <div
            className="m-0"
            dangerouslySetInnerHTML={{
              __html: stringToHtml(
                t(
                  'administration.blocks.type.evaluation.helpers.whatIsThisMessage.part3'
                )
              ),
            }}
          />
        </div>
        <p>
          {t(
            'administration.blocks.type.evaluation.helpers.whatIsThisMessage.disclaimer'
          )}
        </p>
      </div>
    )
  }

  return (
    <div className={`${styles.container}`}>
      <div>
        <InputLabel
          label={`${t(
            'administration.blocks.type.evaluation.question.singular'
          )} ${number}`}
        />
        <textarea
          rows={1}
          id="text-area"
          value={data['text']}
          className="w-100 form-control"
          style={{ resize: 'none', overflow: 'hidden' }}
          onChange={(e) => setData({ ...data, ['text']: e.target.value })}
        />
      </div>
      <div>
        {!linkOptionSelected ? (
          <>
            <Button
              size={ButtonSizes.Small}
              className={styles.linkButton}
              onClick={() => setLinkOptionSelected(1)}
            >
              {t('administration.blocks.type.evaluation.link')}
              <FontAwesomeIcon icon={faPlus} className="ml-1" />
            </Button>
            <a
              className={styles.helpers}
              onClick={() => setShowHelpModal(true)}
            >
              {t('administration.blocks.type.evaluation.helpers.whatIsThis')}
            </a>
          </>
        ) : (
          <div className={styles.openLinkContainer}>
            <Button
              className={`add-link-button ${styles.roundedIconButton}`}
              variant={ButtonVariant.Secondary}
              onClick={() => setLinkOptionSelected(0)}
            >
              <span className={styles.closeLinkText}>
                {t('administration.blocks.type.evaluation.link')}
                <FontAwesomeIcon
                  className="ml-1"
                  icon={faTimes}
                ></FontAwesomeIcon>
              </span>
            </Button>

            <div className="d-flex align-items-center">
              <TextInput
                name={'link'}
                value={data['link'].url}
                label=""
                placeHolder=""
                onChange={(e) =>
                  setData({
                    ...data,
                    link: { ...data.link, url: e.target.value },
                  })
                }
                disabled={false}
                error={''}
              />
              <CommonToolTip
                text={t('administration.blocks.type.evaluation.tooltips.video')}
                position="top"
              >
                <div>
                  <Button
                    size={ButtonSizes.Small}
                    className={`option-video ${styles.linkButton} ${
                      styles.linkOptionButton
                    } ${
                      linkOptionSelected === 1
                        ? styles.linkOptionButtonActive
                        : ''
                    } m-0 ml-2 p-2`}
                    onClick={() => setLinkOptionSelected(1)}
                  >
                    <FontAwesomeIcon icon={faVideo} />
                  </Button>
                </div>
              </CommonToolTip>
              <CommonToolTip
                text={t('administration.blocks.type.evaluation.tooltips.image')}
                position="top"
              >
                <div>
                  <Button
                    size={ButtonSizes.Small}
                    className={`option-image ${styles.linkButton} ${
                      styles.linkOptionButton
                    } ${
                      linkOptionSelected === 2
                        ? styles.linkOptionButtonActive
                        : ''
                    } m-0 ml-2 p-2`}
                    onClick={() => setLinkOptionSelected(2)}
                  >
                    <FontAwesomeIcon icon={faImage} />
                  </Button>
                </div>
              </CommonToolTip>
              <CommonToolTip
                text={t('administration.blocks.type.evaluation.tooltips.code')}
                position="top"
              >
                <div>
                  <Button
                    size={ButtonSizes.Small}
                    className={`option-code ${styles.linkButton} ${
                      styles.linkOptionButton
                    } ${
                      linkOptionSelected === 3
                        ? styles.linkOptionButtonActive
                        : ''
                    } m-0 ml-2 p-2`}
                    onClick={() => setLinkOptionSelected(3)}
                  >
                    <FontAwesomeIcon icon={faCode} />
                  </Button>
                </div>
              </CommonToolTip>
            </div>
          </div>
        )}
      </div>
      <div className="mt-2">
        <InputLabel
          label={t('administration.blocks.type.evaluation.options')}
        />
        <div className={styles.legend}>
          {t('administration.blocks.type.evaluation.instructions')}
          <Button
            outline={true}
            onClick={() => setShowDescriptionQuestion(!showDescriptionQuestion)}
            className={styles.descriptionButton}
          >
            <FontAwesomeIcon icon={faPen} className="ml-1" />
            {t('administration.blocks.type.evaluation.addExplanationAnswer')}
          </Button>
        </div>
        <div className="mt-2">
          {options.map((option: QuestionOptionForm, index) => (
            <div
              key={'edit-option' + question?.id + '/' + index}
              className={`${styles.optionRow} option-row position-relative`}
            >
              <span className={styles.optionText}>{`${t(
                'administration.blocks.type.evaluation.option'
              )} ${Alphabet[index]}`}</span>
              <div className={styles.inputContainer}>
                <input
                  className={styles.transparentInput}
                  value={option.text}
                  onChange={(e) => handleInputChange(e.target.value, index)}
                />
                <Button
                  className={`${styles.iconButton} ${
                    option.checked ? styles.checked : ''
                  }`}
                  variant={ButtonVariant.Secondary}
                  onClick={() => handleCheckChange(index)}
                >
                  <span>
                    <FontAwesomeIcon icon={faCheckCircle}></FontAwesomeIcon>
                  </span>
                </Button>
              </div>
              {index > 1 && (
                <div className={styles.deleteOption}>
                  <Button
                    className={`add-link-button ${styles.roundedIconButton}`}
                    variant={ButtonVariant.Secondary}
                    onClick={() => deleteOption(index)}
                  >
                    <span>
                      <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
                    </span>
                  </Button>
                </div>
              )}
            </div>
          ))}

          {(showDescriptionQuestion || question?.explanationAnswer) && (
            <div className={styles.descriptionTextArea}>
              <TextAreaInput
                name={'explanationAnswer'}
                value={data['explanationAnswer']}
                onChange={(e) =>
                  setData({ ...data, ['explanationAnswer']: e.target.value })
                }
                rows={3}
                label={`${t(
                  'administration.blocks.type.evaluation.explanationAnswer'
                )}`}
                error={''}
              />
            </div>
          )}
        </div>
        <div className="mt-2">
          {options.length < 5 ? (
            <Button
              outline={true}
              block
              color={ButtonColors.Green}
              className={styles.addOptionButton}
              onClick={addOption}
              disabled={options.length > 4}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t('administration.blocks.type.evaluation.addOption')}
            </Button>
          ) : (
            <div className={styles.offset}></div>
          )}
        </div>
      </div>
      <div>
        <ButtonToolbar className="float-right mt-4">
          <Button
            onClick={handleDiscard}
            variant={ButtonVariant.Secondary}
            size={ButtonSizes.Small}
          >
            {t('administration.blocks.type.evaluation.buttons.discard')}
          </Button>
          <Button
            className="mr-0"
            size={ButtonSizes.Small}
            variant={ButtonVariant.Success}
            onClick={handleSave}
          >
            {t('administration.blocks.type.evaluation.buttons.save')}
          </Button>
        </ButtonToolbar>
      </div>
      {showHelpModal && (
        <EmptyModal
          show={showHelpModal}
          title={t('administration.blocks.type.evaluation.helpers.whatIsThis')}
          text={modalMessage()}
          onClose={() => setShowHelpModal(false)}
          showButtons={false}
        />
      )}
    </div>
  )
}

export default EvaluationQuestionsEditor
